/**
 * GPS → image (%) transform using 2 or 3 anchor points.
 *
 * Each anchor: { lat, lng, x, y }  where x/y are 0..100 image percentages.
 *
 * - 3 anchors → solve 6-parameter affine transform (handles rotation + scale + skew):
 *     x = a*lng + b*lat + c
 *     y = d*lng + e*lat + f
 *
 * - 2 anchors → linear stretch (assumes map is north-up, no rotation):
 *     x = a*lng + b
 *     y = c*lat + d
 *
 * - <2 anchors → returns null (not enough info).
 */

function solve3x3(M, v) {
  // Gaussian elimination on a 3×3 augmented matrix. Returns [x0,x1,x2] or null.
  const A = M.map((row, i) => [...row, v[i]]);
  const n = 3;
  for (let i = 0; i < n; i++) {
    // Pivot
    let maxRow = i;
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(A[k][i]) > Math.abs(A[maxRow][i])) maxRow = k;
    }
    [A[i], A[maxRow]] = [A[maxRow], A[i]];
    if (Math.abs(A[i][i]) < 1e-12) return null; // singular
    for (let k = i + 1; k < n; k++) {
      const f = A[k][i] / A[i][i];
      for (let j = i; j <= n; j++) A[k][j] -= f * A[i][j];
    }
  }
  const out = [0, 0, 0];
  for (let i = n - 1; i >= 0; i--) {
    let s = A[i][n];
    for (let j = i + 1; j < n; j++) s -= A[i][j] * out[j];
    out[i] = s / A[i][i];
  }
  return out;
}

export function gpsToImagePct(anchors, lat, lng) {
  if (!anchors || anchors.length < 2) return null;

  if (anchors.length >= 3) {
    // Affine: solve [lng_i, lat_i, 1] * [a,b,c]^T = x_i  and similarly for y
    const M = anchors.slice(0, 3).map((a) => [a.lng, a.lat, 1]);
    const vx = anchors.slice(0, 3).map((a) => a.x);
    const vy = anchors.slice(0, 3).map((a) => a.y);
    const cx = solve3x3(M, vx);
    const cy = solve3x3(M, vy);
    if (!cx || !cy) return null;
    return {
      x: cx[0] * lng + cx[1] * lat + cx[2],
      y: cy[0] * lng + cy[1] * lat + cy[2],
    };
  }

  // 2-point linear (no rotation)
  const [a1, a2] = anchors;
  const dx = a2.x - a1.x;
  const dlng = a2.lng - a1.lng;
  const dy = a2.y - a1.y;
  const dlat = a2.lat - a1.lat;
  if (Math.abs(dlng) < 1e-12 || Math.abs(dlat) < 1e-12) return null;
  const ax = dx / dlng;
  const bx = a1.x - ax * a1.lng;
  const ay = dy / dlat;
  const by = a1.y - ay * a1.lat;
  return { x: ax * lng + bx, y: ay * lat + by };
}
